import { MenuItemFormData } from "@/components/EnhancedMenuItemDialogForm.stateless";
import { db, analytics } from "@/firebase";
import { logEvent } from "firebase/analytics";
import { MenuItem } from "@/model/entities";
import {
  collection,
  getDocs,
  where,
  query,
  addDoc,
  doc,
  setDoc,
  deleteDoc,
  writeBatch,
  getDoc,
  orderBy,
  updateDoc,
} from "firebase/firestore";
import { uploadImage } from "./firebaseUtils";
import { toast } from "sonner";
import i18n from "i18next";

export const menuItemService = {
  fetchAllByRestaurantId: async (restaurantId: string) => {
    try {
      console.info("Fetching menu items for restaurant:", restaurantId);
      logEvent(analytics, "fetch_menu_items", { restaurantId });
      const itemsRef = collection(db, "menuItems");
      const q = query(
        itemsRef,
        where("restaurantId", "==", restaurantId),
        orderBy("order", "asc"),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as MenuItem,
      );
    } catch (error) {
      console.error(
        `[menuItemService.fetchAllByRestaurantId] Failed to fetch menu items for restaurant ${restaurantId}:`,
        error,
      );
      toast.error(i18n.t("errors.fetchMenuItemsFailed"));
      return [];
    }
  },

  fetchAllByCategoryId: async (categoryId: string) => {
    try {
      console.info("Fetching menu items for category:", categoryId);
      logEvent(analytics, "fetch_menu_items_by_category", { categoryId });
      const itemsRef = collection(db, "menuItems");
      const q = query(
        itemsRef,
        where("categoryId", "==", categoryId),
        orderBy("order", "asc"),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as MenuItem,
      );
    } catch (error) {
      console.error(
        `[menuItemService.fetchAllByCategoryId] Failed to fetch menu items for category ${categoryId}:`,
        error,
      );
      toast.error(i18n.t("errors.fetchMenuItemsFailed"));
      return [];
    }
  },

  getById: async (id: string) => {
    try {
      const itemRef = doc(db, "menuItems", id);
      const itemDoc = await getDoc(itemRef);
      return { id: itemDoc.id, ...itemDoc.data() } as MenuItem;
    } catch (error) {
      console.error(
        `[menuItemService.getById] Failed to fetch menu item with id ${id}:`,
        error,
      );
      return null;
    }
  },

  create: (data: MenuItem) => {
    const itemsRef = collection(db, "menuItems");
    // Await the addDoc call and return a plain object with the generated id
    // to avoid returning a DocumentReference (which includes functions and
    // non-serializable data) to callers (e.g., react-query cache / devtools).
    return addDoc(itemsRef, data).then(
      (docRef) => ({ id: docRef.id, ...data }) as MenuItem,
    );
  },
  update: (id: string, data: MenuItem) => {
    const itemRef = doc(db, "menuItems", id);
    return setDoc(itemRef, data, { merge: true });
  },
  updateMany: async (items: MenuItem[]) => {
    const itemsToUpdate = items.map((item, index) => {
      return { ...item, order: index };
    });

    const batch = writeBatch(db);

    itemsToUpdate.forEach((item) => {
      if (typeof item.id === "string") {
        const menuItemsRef = doc(db, "menuItems", item.id);
        batch.update(menuItemsRef, { ...item });
      }
    });

    await batch.commit();
  },
  delete: (id: string) => {
    const itemRef = doc(db, "menuItems", id);
    return deleteDoc(itemRef);
  },

  createMenuItem: async (restaurantId: string, data: MenuItemFormData) => {
    let imageUrl = "";
    const { image, isAvailable, description, ...rest } = data;
    const file = image?.[0] ?? undefined;
    if (file && typeof file !== "string") {
      const path = `menuItems/${restaurantId}/${Date.now()}_${file.name}`;
      imageUrl = await uploadImage(file, path);
    }

    const menuItem: MenuItem = {
      ...rest,
      imageUrl: imageUrl || (typeof image === "string" ? image : ""),
      restaurantId,
      isAvailable: isAvailable ?? true,
      description: description || "",
    };
    console.log("Creating menu item:", menuItem);
    const itemsRef = collection(db, "menuItems");
    const docRef = await addDoc(itemsRef, menuItem);
    // Return the created item as a plain object (including id)
    return { id: docRef.id, ...menuItem } as MenuItem;
  },

  updateMenuItem: async (
    restaurantId: string,
    itemId: string,
    data: MenuItemFormData,
  ) => {
    let imageUrl: string | undefined = undefined;
    const { image, isAvailable, ...rest } = data;
    if (image && typeof image !== "string") {
      const file = image[0];
      const path = `menuItems/${restaurantId}/${Date.now()}_${file.name}`;
      imageUrl = await uploadImage(file, path);
    }

    const originalItem = await menuItemService.getById(itemId);
    if (!originalItem) {
      throw new Error(`Menu item with id ${itemId} not found`);
    }

    if (data.categoryId !== originalItem.categoryId) {
      const maxOrder = await menuItemService
        .fetchAllByCategoryId(data.categoryId)
        .then((items) =>
          items.reduce(
            (max, item) => (item.order && item.order > max ? item.order : max),
            0,
          ),
        );
      rest.order = maxOrder + 1;
    }

    const menuItem: Partial<MenuItem> = {
      ...rest,
      // Only include isAvailable if it's explicitly provided to avoid
      // writing `undefined` into Firestore which causes an error.
      ...(typeof isAvailable === "boolean" ? { isAvailable } : {}),
      imageUrl: imageUrl ?? (typeof image === "string" ? image : undefined),
    };

    if (!data.id) throw new Error("Menu item id is required for update");
    const itemRef = doc(db, "menuItems", itemId);
    await setDoc(itemRef, menuItem, { merge: true });
    return { id: itemId, ...menuItem } as MenuItem;
  },

  // Toggle availability state of a menu item
  toggleAvailability: async (
    itemId: string,
    isAvailable: boolean,
  ): Promise<MenuItem> => {
    const itemRef = doc(db, "menuItems", itemId);
    await updateDoc(itemRef, { isAvailable });
    const snapshot = await getDoc(itemRef);
    return { id: snapshot.id, ...(snapshot.data() as MenuItem) };
  },
};
